import { Page } from '../types';
import { DrawingState, blankDrawingState } from '../../drawing/reducers/drawing';
import { takeSnapshot } from '../../reducers/history';
import { EntireComposerState } from './composer';
import { moveToPage } from './page';

const maxHistoryRecords = 10;

export enum ComposerSnapshotKind {
  Clipbook,
  Drawing,
}

export interface ComposerSnapshotClipbook {
  readonly kind: ComposerSnapshotKind.Clipbook;
  readonly name: string;
  readonly pages: ReadonlyArray<Page>;
}

export interface ComposerSnapshotDrawing {
  readonly kind: ComposerSnapshotKind.Drawing;
  readonly page: number;
  readonly data: DrawingState;
}

export type ComposerSnapshot = ComposerSnapshotClipbook | ComposerSnapshotDrawing;

export function takeClipbookSnapshot(state: EntireComposerState): EntireComposerState {
  const { clipbook } = state;
  if (clipbook === null) {
    throw new Error('Illegal state.');
  }

  const history = takeSnapshot(
    state.history,
    {
      kind: ComposerSnapshotKind.Clipbook,
      name: clipbook.name,
      pages: clipbook.pages,
    },
    maxHistoryRecords,
  );

  return { ...state, history };
}

export function takeDrawingSnapshot(state: EntireComposerState): EntireComposerState {
  const { clipbook } = state;
  if (clipbook === null) {
    throw new Error('Illegal state.');
  }

  const { currentPage } = clipbook;
  if (currentPage === null) {
    throw new Error('Illegal state.');
  }

  const pageID = clipbook.pages[currentPage].uniqueID;
  const history = takeSnapshot(
    state.history,
    {
      kind: ComposerSnapshotKind.Drawing,
      data: pageID in state.drawings ? state.drawings[pageID] : blankDrawingState,
      page: currentPage,
    },
    maxHistoryRecords,
  );

  return { ...state, history };
}

export function consumeSnapshot(
  state: EntireComposerState,
  snapshot: ComposerSnapshot,
): EntireComposerState {
  const { clipbook } = state;
  if (clipbook === null) {
    throw new Error('Illegal state.');
  }

  switch (snapshot.kind) {
    case ComposerSnapshotKind.Clipbook: {
      const intermediateState = {
        ...state,
        clipbook: {
          ...clipbook,
          name: snapshot.name,
          pages: snapshot.pages,
        },
      };

      const { currentPage } = clipbook;
      if (currentPage !== null && currentPage >= snapshot.pages.length) {
        return moveToPage(intermediateState, snapshot.pages.length - 1);
      }

      if (clipbook.pages.length < snapshot.pages.length) {
        // Assumes that page addition is redone or page removal is undone.
        for (let index = 0; index < clipbook.pages.length; index += 1) {
          if (clipbook.pages[index].uniqueID !== clipbook.pages[index].uniqueID) {
            return moveToPage(intermediateState, index);
          }
        }

        return moveToPage(intermediateState, clipbook.pages.length);
      }

      return intermediateState;
    }

    case ComposerSnapshotKind.Drawing: {
      const pageID = clipbook.pages[snapshot.page].uniqueID;
      const drawings: { [pageID: string]: DrawingState } = { ...state.drawings };
      drawings[pageID] = snapshot.data;

      const intermediateState = { ...state, drawings };
      return moveToPage(intermediateState, snapshot.page);
    }
  }
}

export function dataReplacedBySnapshot(
  state: EntireComposerState,
  target: ComposerSnapshot,
): ComposerSnapshot {
  const { clipbook } = state;
  if (clipbook === null) {
    throw new Error('Illegal state.');
  }

  switch (target.kind) {
    case ComposerSnapshotKind.Clipbook:
      return {
        ...target,
        name: clipbook.name,
        pages: clipbook.pages,
      };

    case ComposerSnapshotKind.Drawing:
      return {
        ...target,
        data: state.drawings[clipbook.pages[target.page].uniqueID],
      };
  }
}
